import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Search,
  Filter,
  ArrowUpDown,
  Loader2,
  Users,
  ChevronDown,
  Zap,
  RefreshCw,
  BarChart3,
} from 'lucide-react';
import SlamFitGauge from './SlamFitGauge';
import UNCPlayerCard from './UNCPlayerCard';
import { CAROLINA_BLUE, ACCENT, RISK_COLORS, RISK_ORDER, getInitials } from '../../config/uncConstants';

// ═══════════════════════════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════════════════════════

const POSITIONS = ['All', 'P', 'C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'DH', 'IF', 'OF', 'UT'];
const YEARS = ['All', 'Fr', 'So', 'Jr', 'Sr', 'R-Fr', 'R-So', 'R-Jr', 'R-Sr'];
const SORT_OPTIONS = [
  { value: 'name', label: 'Name' },
  { value: 'number', label: 'Number' },
  { value: 'position', label: 'Position' },
  { value: 'fitScore', label: 'SLAM Fit' },
  { value: 'transferRisk', label: 'Transfer Risk' },
];

const GRADE_COLORS = {
  'A+': '#00CC66', 'A': '#00CC66', 'A-': '#22C55E',
  'B+': '#84CC16', 'B': '#F59E0B', 'B-': '#F59E0B',
  'C+': '#F97316', 'C': '#F97316', 'C-': '#EF4444',
  'D': '#EF4444', 'F': '#DC2626',
};

// ═══════════════════════════════════════════════════════════════════════════
// SKELETON
// ═══════════════════════════════════════════════════════════════════════════

function SkeletonCard() {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900 p-4 animate-pulse">
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-zinc-800" />
        <div className="flex-1 space-y-2">
          <div className="h-4 w-28 rounded bg-zinc-800" />
          <div className="h-3 w-20 rounded bg-zinc-800" />
        </div>
        <div className="w-10 h-10 rounded-full bg-zinc-800" />
      </div>
      <div className="mt-3 h-2 w-full rounded bg-zinc-800" />
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// PLAYER CARD (grid item) — shows real grade + risk + fit bar
// ═══════════════════════════════════════════════════════════════════════════

function PlayerGridCard({ player, evaluation, onClick }) {
  const [imgErr, setImgErr] = useState(false);
  const fitScore = evaluation?.slamFitScore ?? evaluation?.slam_fit_score ?? 0;
  const risk = (evaluation?.transferRisk ?? evaluation?.transfer_risk ?? 'LOW').toUpperCase();
  const grade = evaluation?.overallGrade ?? evaluation?.overall_grade ?? null;
  const initials = getInitials(player.name);

  return (
    <button
      onClick={onClick}
      className="w-full text-left rounded-xl border border-zinc-800 bg-zinc-900 p-4 hover:border-zinc-700 hover:bg-zinc-900/80 transition-all duration-300 group"
    >
      <div className="flex items-center gap-3">
        {/* Headshot */}
        {player.headshot_url && !imgErr ? (
          <img
            src={player.headshot_url}
            alt={player.name}
            className="w-12 h-12 rounded-full object-cover border-2 border-zinc-800 group-hover:border-[#7BAFD4] transition-colors"
            onError={() => setImgErr(true)}
          />
        ) : (
          <div
            className="w-12 h-12 rounded-full flex items-center justify-center font-bold uppercase text-sm border-2 border-zinc-800 group-hover:border-[#7BAFD4] transition-colors"
            style={{ background: CAROLINA_BLUE, color: '#000' }}
          >
            {initials}
          </div>
        )}

        {/* Name + meta */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-medium text-white truncate group-hover:text-[#7BAFD4] transition-colors">
            {player.name}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-zinc-500 mt-0.5">
            {player.number && <span style={{ color: CAROLINA_BLUE }}>#{player.number}</span>}
            {player.position && <span>{player.position}</span>}
            {player.year && <span>- {player.year}</span>}
          </div>
        </div>

        {/* Mini gauge + grade badge */}
        <div className="flex items-center gap-2 shrink-0">
          {grade && (
            <span
              className="text-[10px] font-bold px-1.5 py-0.5 rounded"
              style={{ color: GRADE_COLORS[grade] || '#888', background: `${GRADE_COLORS[grade] || '#888'}18` }}
            >
              {grade}
            </span>
          )}
          <SlamFitGauge score={fitScore} size={40} strokeWidth={3} />
        </div>
      </div>

      {/* Bottom row: mini fit bar + risk pill */}
      <div className="mt-3 flex items-center justify-between">
        <div className="flex-1 mr-3">
          <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: `${Math.min(fitScore, 100)}%`,
                background: fitScore >= 70 ? '#00CC66' : fitScore >= 40 ? '#F59E0B' : '#FF4444',
              }}
            />
          </div>
        </div>
        <span
          className="text-[9px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full"
          style={{
            color: RISK_COLORS[risk] || RISK_COLORS.LOW,
            background: `${RISK_COLORS[risk] || RISK_COLORS.LOW}18`,
          }}
        >
          {risk}
        </span>
      </div>
    </button>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// UNCRosterGrid — Unified Roster + Evaluations view
// ═══════════════════════════════════════════════════════════════════════════

export default function UNCRosterGrid() {
  const [roster, setRoster] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Evaluation state
  const [evalLoading, setEvalLoading] = useState(false);
  const [evalProgress, setEvalProgress] = useState(null); // { completed, total }
  const [lastEvalTime, setLastEvalTime] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [posFilter, setPosFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('All');
  const [fitRange, setFitRange] = useState([0, 100]);
  const [sortBy, setSortBy] = useState('fitScore');
  const [sortDir, setSortDir] = useState('desc');

  // Modal
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // ── Fetch roster + stored evaluations ──
  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rosterRes, evalRes] = await Promise.all([
        fetch('/api/unc/roster'),
        fetch('/api/unc/stored-evaluations'),
      ]);

      if (!rosterRes.ok) throw new Error(`Roster fetch failed (${rosterRes.status})`);
      const rosterJson = await rosterRes.json();
      const rosterPayload = rosterJson?.data || rosterJson;

      let evalMap = {};
      if (evalRes.ok) {
        const evalJson = await evalRes.json();
        const evalPayload = evalJson?.data || evalJson;
        evalMap = evalPayload?.evaluations || evalPayload || {};
      }

      setRoster(Array.isArray(rosterPayload) ? rosterPayload : rosterPayload?.players || []);
      setEvaluations(evalMap);

      // Check when last evaluation was run
      const timestamps = Object.values(evalMap).map(e => e?.evaluatedAt).filter(Boolean);
      if (timestamps.length > 0) {
        setLastEvalTime(new Date(Math.max(...timestamps.map(t => new Date(t).getTime()))));
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Evaluate All ──
  const handleEvaluateAll = useCallback(async () => {
    setEvalLoading(true);
    setEvalProgress({ completed: 0, total: roster.length });
    try {
      const res = await fetch('/api/unc/roster-evaluate', { method: 'POST' });
      if (!res.ok) throw new Error(`Evaluation failed (${res.status})`);
      const data = await res.json();
      const payload = data?.data || data;
      const evalMap = payload?.evaluations || {};

      setEvaluations(evalMap);
      setEvalProgress({ completed: Object.keys(evalMap).length, total: roster.length });
      setLastEvalTime(new Date());
    } catch (err) {
      console.error('[UNCRosterGrid] Evaluate All error:', err);
    } finally {
      setEvalLoading(false);
      setTimeout(() => setEvalProgress(null), 2000);
    }
  }, [roster.length]);

  // ── Sync Roster from GoHeels.com ──
  const [syncLoading, setSyncLoading] = useState(false);
  const handleSyncRoster = useCallback(async () => {
    setSyncLoading(true);
    try {
      const res = await fetch('/api/unc/roster/refresh', { method: 'POST' });
      if (!res.ok) throw new Error(`Sync failed (${res.status})`);
      await loadData();
    } catch (err) {
      console.error('[UNCRosterGrid] Roster sync error:', err);
    } finally {
      setSyncLoading(false);
    }
  }, [loadData]);

  // ── Evaluate Single Player (from player card) ──
  const handleEvaluateSingle = useCallback(async (playerName) => {
    try {
      const res = await fetch('/api/unc/evaluate-single', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ playerName }),
      });
      if (!res.ok) return;
      const data = await res.json();
      const ev = data?.data?.evaluation || data?.evaluation;
      if (ev?.playerId) {
        setEvaluations(prev => ({ ...prev, [ev.playerId]: ev }));
      }
    } catch (err) {
      console.error('[UNCRosterGrid] Single eval error:', err);
    }
  }, []);

  // ── KPI Summary ──
  const summary = useMemo(() => {
    const evals = Object.values(evaluations);
    if (evals.length === 0) return null;
    const scores = evals.map(e => e?.slamFitScore ?? 0).filter(s => s > 0);
    return {
      evaluated: evals.length,
      avgFit: scores.length > 0 ? Math.round(scores.reduce((a, b) => a + b, 0) / scores.length) : 0,
      highRisk: evals.filter(e => (e?.transferRisk || '').toUpperCase() === 'HIGH').length,
      topGrade: evals.filter(e => (e?.overallGrade || '').startsWith('A')).length,
    };
  }, [evaluations]);

  // ── Filtered + Sorted ──
  const displayed = useMemo(() => {
    let list = [...roster];

    if (search) {
      const q = search.toLowerCase();
      list = list.filter((p) => (p.name || '').toLowerCase().includes(q));
    }
    if (posFilter !== 'All') {
      list = list.filter((p) => (p.position || '').toUpperCase().includes(posFilter.toUpperCase()));
    }
    if (yearFilter !== 'All') {
      list = list.filter((p) => (p.year || '').toLowerCase() === yearFilter.toLowerCase());
    }
    list = list.filter((p) => {
      const pid = p.id || p.playerId || p.player_id || p.name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const ev = evaluations[pid];
      const score = ev?.slamFitScore ?? ev?.slam_fit_score ?? 0;
      return score >= fitRange[0] && score <= fitRange[1];
    });

    list.sort((a, b) => {
      const pidA = a.id || a.playerId || a.player_id || a.name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const pidB = b.id || b.playerId || b.player_id || b.name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
      const evA = evaluations[pidA] || {};
      const evB = evaluations[pidB] || {};
      let cmp = 0;
      switch (sortBy) {
        case 'name': cmp = (a.name || '').localeCompare(b.name || ''); break;
        case 'number': cmp = (parseInt(a.number) || 999) - (parseInt(b.number) || 999); break;
        case 'position': cmp = (a.position || '').localeCompare(b.position || ''); break;
        case 'fitScore': cmp = (evA.slamFitScore ?? 0) - (evB.slamFitScore ?? 0); break;
        case 'transferRisk': {
          const rA = (evA.transferRisk ?? 'LOW').toUpperCase();
          const rB = (evB.transferRisk ?? 'LOW').toUpperCase();
          cmp = (RISK_ORDER[rA] ?? 2) - (RISK_ORDER[rB] ?? 2);
          break;
        }
        default: break;
      }
      return sortDir === 'desc' ? -cmp : cmp;
    });

    return list;
  }, [roster, evaluations, search, posFilter, yearFilter, fitRange, sortBy, sortDir]);

  // Helper to resolve player → evalMap key
  const getEvalKey = (p) =>
    p.id || p.playerId || p.player_id || p.name?.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

  // ── Render ──
  return (
    <div className="h-full flex flex-col bg-black">
      {/* KPI Strip + Evaluate Button */}
      <div className="shrink-0 px-5 pt-5 pb-3">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center font-bold text-sm"
              style={{ background: CAROLINA_BLUE, color: '#000' }}
            >
              UNC
            </div>
            <div>
              <h1 className="text-lg font-semibold text-white">UNC Tar Heels Baseball</h1>
              <p className="text-xs text-zinc-500">
                {roster.length} players
                {lastEvalTime && ` · Last evaluated ${timeAgo(lastEvalTime)}`}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleSyncRoster}
              disabled={syncLoading || evalLoading || loading}
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 hover:brightness-110 border border-zinc-700 bg-zinc-800 text-zinc-300"
            >
              {syncLoading ? (
                <><Loader2 size={14} className="animate-spin" /> Syncing...</>
              ) : (
                <><RefreshCw size={14} /> Sync Roster</>
              )}
            </button>
            <button
              onClick={handleEvaluateAll}
              disabled={evalLoading || loading}
              className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all disabled:opacity-50 hover:brightness-110"
              style={{ background: ACCENT, color: '#000' }}
            >
              {evalLoading ? (
                <><Loader2 size={14} className="animate-spin" /> Evaluating...</>
              ) : (
                <><Zap size={14} /> Evaluate All</>
              )}
            </button>
          </div>
        </div>

        {/* Progress bar (during evaluation) */}
        {evalProgress && (
          <div className="mb-3">
            <div className="flex items-center justify-between text-[10px] text-zinc-500 mb-1">
              <span>{evalLoading ? 'Computing SLAM Fit Scores...' : 'Evaluation complete'}</span>
              <span>{evalProgress.completed} / {evalProgress.total}</span>
            </div>
            <div className="h-1.5 rounded-full bg-zinc-800 overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${evalProgress.total > 0 ? (evalProgress.completed / evalProgress.total) * 100 : 0}%`,
                  background: evalLoading ? CAROLINA_BLUE : '#22C55E',
                }}
              />
            </div>
          </div>
        )}

        {/* KPI Strip */}
        {summary && (
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800">
              <BarChart3 size={14} className="text-zinc-500" />
              <div>
                <div className="text-sm font-semibold text-white">{summary.avgFit}</div>
                <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Avg Fit</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800">
              <Users size={14} className="text-zinc-500" />
              <div>
                <div className="text-sm font-semibold text-white">{summary.evaluated}</div>
                <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Evaluated</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800">
              <Zap size={14} style={{ color: '#22C55E' }} />
              <div>
                <div className="text-sm font-semibold" style={{ color: '#22C55E' }}>{summary.topGrade}</div>
                <div className="text-[9px] text-zinc-500 uppercase tracking-wider">A-Grade</div>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-900 border border-zinc-800">
              <RefreshCw size={14} style={{ color: summary.highRisk > 0 ? '#EF4444' : '#888' }} />
              <div>
                <div className="text-sm font-semibold" style={{ color: summary.highRisk > 0 ? '#EF4444' : '#fff' }}>{summary.highRisk}</div>
                <div className="text-[9px] text-zinc-500 uppercase tracking-wider">Portal Risk</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Filters bar */}
      <div className="shrink-0 px-5 pb-4 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[180px] max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            type="text"
            placeholder="Search by name..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-zinc-900 border border-zinc-800 text-white placeholder-zinc-600 focus:outline-none focus:border-zinc-600 transition-colors"
          />
        </div>

        <div className="relative">
          <select
            value={posFilter}
            onChange={(e) => setPosFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 text-xs rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 focus:outline-none focus:border-zinc-600 transition-colors cursor-pointer"
          >
            {POSITIONS.map((p) => (
              <option key={p} value={p}>{p === 'All' ? 'All Positions' : p}</option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
        </div>

        <div className="relative">
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 text-xs rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 focus:outline-none focus:border-zinc-600 transition-colors cursor-pointer"
          >
            {YEARS.map((y) => (
              <option key={y} value={y}>{y === 'All' ? 'All Years' : y}</option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
        </div>

        <div className="flex items-center gap-2 text-[10px] text-zinc-500">
          <Filter size={12} />
          <span>Fit:</span>
          <input type="range" min={0} max={100} value={fitRange[0]}
            onChange={(e) => setFitRange([+e.target.value, fitRange[1]])} className="w-16 accent-[#7BAFD4]" />
          <span className="text-zinc-400">{fitRange[0]}-{fitRange[1]}</span>
          <input type="range" min={0} max={100} value={fitRange[1]}
            onChange={(e) => setFitRange([fitRange[0], +e.target.value])} className="w-16 accent-[#7BAFD4]" />
        </div>

        <div className="relative">
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="appearance-none pl-3 pr-8 py-2 text-xs rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-300 focus:outline-none focus:border-zinc-600 transition-colors cursor-pointer"
          >
            {SORT_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          <ChevronDown size={12} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
        </div>

        <button
          onClick={() => setSortDir((d) => d === 'asc' ? 'desc' : 'asc')}
          className="p-2 rounded-lg bg-zinc-900 border border-zinc-800 text-zinc-400 hover:text-white hover:border-zinc-600 transition-colors"
          title={`Sort ${sortDir === 'asc' ? 'ascending' : 'descending'}`}
        >
          <ArrowUpDown size={14} />
        </button>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-y-auto px-5 pb-5" style={{ scrollbarWidth: 'thin' }}>
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {Array.from({ length: 12 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <Users size={32} className="mb-3 text-zinc-700" />
            <p className="text-sm">{error}</p>
          </div>
        ) : displayed.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-zinc-500">
            <Users size={32} className="mb-3 text-zinc-700" />
            <p className="text-sm">No players match your filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {displayed.map((p) => {
              const pid = getEvalKey(p);
              return (
                <PlayerGridCard
                  key={pid}
                  player={p}
                  evaluation={evaluations[pid]}
                  onClick={() => setSelectedPlayer(p)}
                />
              );
            })}
          </div>
        )}
      </div>

      {/* Player Modal */}
      {selectedPlayer && (
        <UNCPlayerCard
          player={selectedPlayer}
          evaluation={evaluations[getEvalKey(selectedPlayer)]}
          isOpen={!!selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          onEvaluate={handleEvaluateSingle}
        />
      )}
    </div>
  );
}

// ── Helper ──
function timeAgo(date) {
  if (!date) return '';
  const ms = Date.now() - new Date(date).getTime();
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}
